/**
 * Toast utility with duplicate filtering
 *
 * Wraps sonner's toast functions to prevent duplicate messages
 * from appearing within a short time window.
 */
import { toast as sonnerToast } from 'sonner';

/** Toast options type inferred from sonner */
type ToastOptions = Parameters<typeof sonnerToast.success>[1];

/** Time window to prevent duplicate toasts (in milliseconds) */
const DEDUPE_WINDOW_MS = 3000;

/** Maximum number of recent messages to track */
const MAX_TRACKED_MESSAGES = 20;

/** Recent message entry */
interface RecentMessage {
  content: string;
  timestamp: number;
}

/** Tracked recent messages */
const recentMessages: RecentMessage[] = [];

/**
 * Check if a message was recently shown
 */
function wasRecentlyShown(content: string): boolean {
  const now = Date.now();

  // Clean up old entries
  while (recentMessages.length > 0 && now - recentMessages[0].timestamp > DEDUPE_WINDOW_MS) {
    recentMessages.shift();
  }

  // Trim if too many
  while (recentMessages.length > MAX_TRACKED_MESSAGES) {
    recentMessages.shift();
  }

  // Check if this content was recently shown
  return recentMessages.some((msg) => msg.content === content);
}

/**
 * Track a message as shown
 */
function trackMessage(content: string): void {
  recentMessages.push({
    content,
    timestamp: Date.now(),
  });
}

/**
 * Get normalized content for deduplication
 */
function getContentKey(message: string | React.ReactNode, options?: ToastOptions): string {
  // Convert to string for comparison
  const messageStr = typeof message === 'string' ? message : String(message);
  const description = options?.description ? String(options.description) : '';
  return `${messageStr}::${description}`;
}

/**
 * Wrapped toast with duplicate filtering
 */
function createFilteredToast<T extends typeof sonnerToast.success>(
  originalFn: T
): (message: string | React.ReactNode, options?: ToastOptions) => ReturnType<T> | undefined {
  return (message: string | React.ReactNode, options?: ToastOptions) => {
    const contentKey = getContentKey(message, options);

    if (wasRecentlyShown(contentKey)) {
      return undefined;
    }

    trackMessage(contentKey);
    return originalFn(message, options) as ReturnType<T>;
  };
}

/**
 * Toast functions with duplicate filtering
 */
export const toast = {
  success: createFilteredToast(sonnerToast.success),
  error: createFilteredToast(sonnerToast.error),
  warning: createFilteredToast(sonnerToast.warning),
  info: createFilteredToast(sonnerToast.info),
  message: createFilteredToast(sonnerToast.message),
  loading: sonnerToast.loading, // Loading toasts shouldn't be deduplicated
  promise: sonnerToast.promise, // Promise toasts shouldn't be deduplicated
  dismiss: sonnerToast.dismiss,
  custom: sonnerToast.custom,
};

export default toast;
